import { ApiClient } from './utils/api';
import { AuthService } from './services/AuthService';
import { PurchaseService } from './services/PurchaseService';
import { TelemetryService } from './services/TelemetryService';
import { RemoteConfigService } from './services/RemoteConfigService';
import { SDKConfig, PurchaseItem, PurchaseURLOptions, LoginURLOptions, Transaction, TipURLOptions } from './types';
import { handleStoreLink, parseDeepLinkParams, isValidURL } from './utils/url';

// Export types
export * from './types';

// API URLs for different environments
const API_URLS: Record<'development' | 'staging' | 'production', string> = {
  development: 'http://localhost:3000',
  staging: 'https://staging.oncade.xyz',
  production: 'https://dev.oncade.xyz',
};

/**
 * Oncade SDK for integrating game purchases
 */
export class OncadeSDK {
  private apiClient: ApiClient;
  private authService: AuthService;
  private purchaseService: PurchaseService;
  private telemetryService?: TelemetryService;
  private remoteConfigService?: RemoteConfigService;
  private config: SDKConfig;
  private apiUrl: string;

  /**
   * Create a new instance of the Oncade SDK
   * @param config SDK configuration
   */
  constructor(config: SDKConfig) {
    this.config = config;
    const env = config.environment ?? 'production';
    this.apiUrl = API_URLS[env];

    /* core plumbing */
    this.apiClient = new ApiClient(this.apiUrl, config.apiKey, config.gameId);
    this.authService = new AuthService(this.apiClient);
    const storeBaseUrl = `${this.apiUrl}/store`;
    this.purchaseService = new PurchaseService(this.apiClient, config.gameId, storeBaseUrl);

    /* NEW ─────────── feature gates */
    if (config.telemetry?.enabled !== false) {
      this.telemetryService = new TelemetryService({
        apiClient: this.apiClient,
        authService: this.authService,
        flushIntervalMs: config.telemetry?.flushIntervalMs,
        maxBatchSize: config.telemetry?.maxBatchSize,
      });
    }

    if (config.remoteConfig?.enabled !== false) {
      this.remoteConfigService = new RemoteConfigService({
        apiClient: this.apiClient,
        pollIntervalMs: config.remoteConfig?.pollIntervalMs,
      });
    }
  }

  /**
   * Initialize the game server by verifying the game ID and API key
   * @returns Promise resolving to a boolean indicating success
   */
  async initialize(): Promise<boolean> {
    // Initialize the game server
    const ok = await this.authService.initializeGameServer(
      this.config.gameId,
      this.config.apiKey
    );

    if (ok) {
      await Promise.all([
        this.telemetryService?.start(),       // pulls build/platform context & primes queue
        this.remoteConfigService?.bootstrap() // fetches dev→test→prod staged config
      ]);
    }

    return ok;
  }

  /* ───────────────────────────────────
   *  PUBLIC TELEMETRY HELPERS
   * ─────────────────────────────────── */
  track<EventPayload extends Record<string, unknown>>(
    eventName: string,
    payload: EventPayload,
    options?: { flushImmediately?: boolean }
  ): void {
    this.telemetryService?.track(eventName, payload, options);
  }

  flushTelemetry(): Promise<void> {
    return this.telemetryService?.flush() ?? Promise.resolve();
  }

  /* ───────────────────────────────────
   *  PUBLIC REMOTE CONFIG HELPERS
   * ─────────────────────────────────── */
  /** Typed getter with optional fallback */
  async getConfig<T>(key: string, defaultValue?: T): Promise<T | undefined> {
    return this.remoteConfigService?.get<T>(key, defaultValue);
  }

  /** Get entire config store */
  async getAllConfig(): Promise<Record<string, unknown>> {
    return this.remoteConfigService?.getConfig() ?? {};
  }

  /** Subscribe to live changes for a single key */
  onConfigChange<T>(key: string, cb: (val: T) => void): () => void {
    return this.remoteConfigService?.subscribe<T>(key, cb) ?? (() => {});
  }

  /**
   * Get session information including user association status
   * @returns Promise resolving to session information including user ID if associated
   */
  async getSessionInfo(): Promise<{
    isValid: boolean;
    hasUserId: boolean;
    sessionToken: string | null;
  }> {
    const sessionInfo = await this.authService.getSessionInfo();
    return {
      ...sessionInfo,
      sessionToken: this.authService.getSessionToken()
    };
  }

  /**
   * Clear the current session data
   */
  clearSession(): void {
    this.authService.clearSession();
  }

  /**
   * Get the store catalog for the game
   * @returns Promise resolving to an array of purchasable items
   */
  async getStoreCatalog(): Promise<PurchaseItem[]> {
    return this.purchaseService.getCatalog();
  }

  /**
   * Get a specific item from the store
   * @param itemId Item ID to retrieve
   * @returns Promise resolving to the item or null if not found
   */
  async getStoreItem(itemId: string): Promise<PurchaseItem | null> {
    return this.purchaseService.getItem(itemId);
  }

  /**
   * Get a purchase URL for an item
   * @param options Purchase URL options
   * @returns The generated purchase URL or null if no valid session token exists
   */
  getPurchaseURL(options: Omit<PurchaseURLOptions, 'sessionToken'> & { sessionToken?: string }): string | null {
    // Get the current session token if not provided in options
    const sessionToken = options.sessionToken || this.authService.getSessionToken();

    // If no session token exists, return null as we can't create a valid purchase URL
    if (!sessionToken) {
      console.error('Cannot generate purchase URL: No valid session token exists');
      return null;
    }

    if (!options.itemId || options.itemId === '' || options.itemId === 'undefined') {
      throw new Error('Item ID is required to generate a purchase URL');
    }

    // Create a new options object with the session token
    const optionsWithToken: PurchaseURLOptions = {
      ...options,
      sessionToken
    };

    return this.purchaseService.generatePurchaseURL(optionsWithToken);
  }

  /**
   * Get purchased items for the current user
   * @returns Promise resolving to an array of purchased items
   */
  async getPurchasedItems(): Promise<PurchaseItem[]> {
    return this.purchaseService.getPurchasedItems();
  }

  /**
   * Get transaction history for the current user
   * @returns Promise resolving to an array of transactions
   */
  async getTransactionHistory(): Promise<Transaction[]> {
    return this.purchaseService.getTransactionHistory();
  }

  /**
   * Extract parameters from a store link
   * @param url URL to extract parameters from
   * @returns Object containing affiliateCode if present
   */
  handleStoreLink(url: string): { affiliateCode?: string } {
    return handleStoreLink(url);
  }

  /**
   * Parse parameters from a deep link URL
   * @param url URL to parse
   * @returns Object containing the parsed parameters
   */
  parseDeepLinkParams(url: string): Record<string, string> {
    return parseDeepLinkParams(url);
  }

  /**
   * Override the API URL (for testing purposes only)
   * @param url The URL to use for API calls
   */
  setApiUrlForTesting(url: string): void {
    this.apiUrl = url;

    // Recreate API client with new URL
    this.apiClient = new ApiClient(
      this.apiUrl,
      this.config.apiKey,
      this.config.gameId
    );

    // Update services
    this.authService = new AuthService(this.apiClient);

    // Update store base URL
    const storeBaseUrl = `${this.apiUrl}/store`;

    this.purchaseService = new PurchaseService(
      this.apiClient,
      this.config.gameId,
      storeBaseUrl
    );
  }

  /**
   * Get a login URL for user authentication and session association
   * @param options Login URL options
   * @returns The generated login URL
   */
  getLoginURL(options: LoginURLOptions): string {
    return this.purchaseService.generateLoginURL(options);
  }

  /**
   * Get a tip URL to allow users to give a tip to the developer
   * @param options Tip URL options
   * @returns The generated tip URL or null if no valid session token exists
   */
  getTipURL(options: Omit<TipURLOptions, 'sessionToken'> & { sessionToken?: string }): string | null {
    // Get the current session token if not provided in options
    const sessionToken = options.sessionToken || this.authService.getSessionToken();

    // If no session token exists, return null as we can't create a valid tip URL
    if (!sessionToken) {
      console.error('Cannot generate tip URL: No valid session token exists');
      return null;
    }

    if (options.amount && options.amount <= 0) {
      throw new Error('Tip amount must be greater than 0');
    }

    if (options.redirectUrl && !isValidURL(options.redirectUrl)) {
      throw new Error('Invalid redirect URL');
    }

    // Create a new options object with the session token and gameId
    const optionsWithToken: TipURLOptions = {
      ...options,
      sessionToken,
      gameId: this.config.gameId
    };

    return this.purchaseService.generateTipURL(optionsWithToken);
  }
}

// Export class directly for better ESM compatibility
export default OncadeSDK;